import { Icon, StatColumn, StatProps, TextVariant, useTextColorMap } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";
import {
    useTokenListContext,
    useLatestCollateralYieldHistoryByMint,
    useVaultApyBreakdown,
    LendingVaultExpanded
} from "@bridgesplit/abf-react";

import { CaptionWithIcon, getCollateralYieldStat, useLendVaultAdditionalStats } from "./common";

export function BaseApy({
    apy,
    additionalStats,
    isAvgApy,
    caption,
    variant = "body1"
}: {
    apy: number | undefined;
    additionalStats?: StatProps[];
    isAvgApy?: boolean;
    caption?: string;
    variant?: TextVariant;
}) {
    const textColorMap = useTextColorMap();
    const baseStats: StatProps[] = [
        {
            value: formatPercent(apy, { formatUndefinedAsNA: true }),
            caption: (
                <CaptionWithIcon
                    variant={variant}
                    caption={caption ?? (isAvgApy ? "Avg. APY" : "Base APY")}
                    iconColor={textColorMap("caption")}
                    icon={<Icon type="base-apy" />}
                />
            ),
            variant: "body2"
        }
    ];
    const stats = additionalStats ? [...baseStats, ...additionalStats] : baseStats;
    return <StatColumn stats={stats} captionVariant={variant} />;
}

export function BaseApyWithCollateralYield({
    apy,
    mint,
    variant = "body1",
    additionalStats = []
}: {
    apy: number | undefined;
    mint: string | undefined;
    variant?: TextVariant;
    additionalStats?: StatProps[];
}) {
    const textColorMap = useTextColorMap();
    const { getMetadata } = useTokenListContext();
    const tokenMetadata = getMetadata(mint);
    const { data: collateralYield } = useLatestCollateralYieldHistoryByMint(mint ? [mint] : []);

    const collateralYieldStat = getCollateralYieldStat(collateralYield?.[mint ?? ""], tokenMetadata, textColorMap);

    return <BaseApy apy={apy} additionalStats={[...additionalStats, ...collateralYieldStat]} variant={variant} />;
}

export function LendVaultBaseApy({ vault }: { vault: LendingVaultExpanded | undefined }) {
    const breakdown = useVaultApyBreakdown({ lendingVaultExpanded: vault });
    const additionalStats = useLendVaultAdditionalStats(vault);

    return <BaseApy apy={breakdown?.baseApy} variant="body2" additionalStats={additionalStats} />;
}
